import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { gsap } from "gsap";
import { isSuperQuickBtnOnGlobal } from "../../config/GlobalVariableConfig";
import { MULTI_POS, REEL_DIVIDER, REEL_FRAME, REEL_NUMBERS, REEL_WIDTH, SYMBOL_HEIGHT, SYMBOL_WIDTH, SYMBOLS_PER_REEL } from "../../config/reelConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { Manager } from "../../core/Manager";


export class ReelCellContainer extends DisplayObject {

    constructor(config: IDisplayObject) {
        super(config);
        this.setPositionResize(Manager.Orientation || "LANDSCAPE");
        this.init();
        this.subscribeToEvents();
    }

    public init() {
        for (let i = 0; i < REEL_NUMBERS; i++) {
            for (let j = 0; j < SYMBOLS_PER_REEL + 1; j++) {
                this.createReelCell(i, j);
            }
        }
    }

    public override destroy(options?: boolean | undefined): void {
        this.unSubscribeToEvents();
        super.destroy(options);
    }

    private subscribeToEvents() {

    }

    private unSubscribeToEvents() {

    }

    private createReelCell(reelIndex: number, rowIndex: number): void {
        const reelCell = Spine.from({
            skeleton: "reelGunSkel",
            atlas: "reelGunAtlas"
        });
        const multiFx = Spine.from({
            skeleton: "reelMultiFxSkel",
            atlas: "reelMultiFxAtlas"
        });
        const multiMergeFx = Spine.from({
            skeleton: "reelMultiMergeSkel",
            atlas: "reelMultiMergeAtlas"
        });
        const multiFxBig = Spine.from({
            skeleton: "multiFxBigSkel",
            atlas: "multiFxBigAtlas"
        });


        if (rowIndex >= SYMBOLS_PER_REEL) {
            return;
        }
        const x =
            REEL_FRAME +
            Math.round(SYMBOL_WIDTH / 2) +
            reelIndex * (REEL_WIDTH + REEL_DIVIDER);

        const y =
            REEL_FRAME +
            Math.round(SYMBOL_HEIGHT / 2) +
            rowIndex * SYMBOL_HEIGHT;

        reelCell.x = x + MULTI_POS;
        reelCell.y = y + MULTI_POS;
        reelCell.label = `cell_${reelIndex}_${rowIndex}`
        reelCell.visible = true;

        multiFx.x = x - 5;
        multiFx.y = y + 30;
        multiFx.label = `multi_${reelIndex}_${rowIndex}`
        multiFx.visible = true;

        multiMergeFx.x = x + MULTI_POS;
        multiMergeFx.y = y + MULTI_POS;
        multiMergeFx.label = `multiMerge_${reelIndex}_${rowIndex}`
        multiMergeFx.visible = true;

        multiFxBig.x = x - 5;
        multiFxBig.y = y + 30;
        multiFxBig.label = `multiFxBig_${reelIndex}_${rowIndex}`
        multiFxBig.visible = true;

        this.addChild(reelCell);
        this.addChild(multiFx);
        this.addChild(multiFxBig);
        this.addChild(multiMergeFx);
    }

    public highlight(col: number, row: number, isBig: boolean = false) {
        const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
        const multi = this.getChildByLabel("multi_" + col + "_" + row) as Spine;
        const multiFxBig = this.getChildByLabel("multiFxBig_" + col + "_" + row) as Spine;
        if (cell) {
            cell.visible = true;
            cell.state.timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
            cell.state.setAnimation(0, "gun_shot", false).listener = {
                complete: () => {
                    cell.state.setAnimation(0, "gun_shot_static", true);
                    cell.state.clearListeners();
                }
            }
            if (isBig) {
                multiFxBig.visible = true;
                multiFxBig.state.timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
                multiFxBig.state.setAnimation(0, "multi_fx_big", false).listener = {
                    complete: () => {
                        multiFxBig.visible = false;
                        multiFxBig.state.clearListeners();
                    }
                };
            } else {
                multi.visible = true;
                multi.state.timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
                multi.state.setAnimation(0, "multi_fx", false).listener = {
                    complete: () => {
                        multi.visible = false;
                        multi.state.clearListeners();
                    }
                };
            }
        }
    }

    public async playMultiMerge(col: number, row: number): Promise<void> {
        const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
        const multiMerge = this.getChildByLabel("multiMerge_" + col + "_" + row) as Spine;
        if (cell) {
            return new Promise<void>((resolve) => {
                multiMerge.visible = true;
                multiMerge.state.timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
                multiMerge.state.setAnimation(0, "multi_marge_fx", false).listener = {
                    complete: () => {
                        multiMerge.visible = false;
                        multiMerge.state.clearListeners();
                        resolve();
                    }
                };
            });
        }
    }

    public hide(col: number, row: number) {
        const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
        const multi = this.getChildByLabel("multi_" + col + "_" + row) as Spine;
        const multiMerge = this.getChildByLabel("multiMerge_" + col + "_" + row) as Spine;
        const multiFxBig = this.getChildByLabel("multiFxBig_" + col + "_" + row) as Spine;

        if (cell) {
            cell.visible = false;
            cell.state.clearListeners();
            multi.visible = false;
            multi.state.clearListeners();
            multiMerge.visible = false;
            multiMerge.state.clearListeners();
            multiFxBig.visible = false;
            multiFxBig.state.clearListeners();
        }
    }

    public makeDark(col: number, row: number) {
        const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
        const multi = this.getChildByLabel("multi_" + col + "_" + row) as Spine;
        const multiMerge = this.getChildByLabel("multiMerge_" + col + "_" + row) as Spine;
        const multiFxBig = this.getChildByLabel("multiFxBig_" + col + "_" + row) as Spine;

        if (cell) {
            cell.tint = "0x222222";
            multi.tint = "0x222222";
            multiMerge.tint = "0x222222";
            multiFxBig.tint = "0x222222";
        }
    }

    public makeUndark() {
        for (let col: number = 0; col < REEL_NUMBERS; col++) {
            for (let row: number = 0; row < SYMBOLS_PER_REEL; row++) {
                const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
                const multi = this.getChildByLabel("multi_" + col + "_" + row) as Spine;
                const multiMerge = this.getChildByLabel("multiMerge_" + col + "_" + row) as Spine;
                const multiFxBig = this.getChildByLabel("multiFxBig_" + col + "_" + row) as Spine;
                if (cell) {
                    cell.tint = "0xffffff";
                    multi.tint = "0xffffff";
                    multiMerge.tint = "0xffffff";
                    multiFxBig.tint = "0xffffff";
                }
            }
        }
    }

    public beforeHide(col: number, row: number) {
        const cell = this.getChildByLabel("cell_" + col + "_" + row) as Spine;
        if (cell) {
            gsap.to(cell.scale, {
                x: 0,
                y: 0,
                duration: 0.8,
                onComplete: () => {
                    cell.scale = 1;
                    cell.visible = false;
                    cell.state.clearListeners();
                }
            });
        }
    }

    public async hideAll() {
        for (let i = 0; i < REEL_NUMBERS; i++) {
            for (let j = 0; j < SYMBOLS_PER_REEL + 1; j++) {
                const cell = this.getChildByLabel("cell_" + i + "_" + j) as Spine;
                const multi = this.getChildByLabel("multi_" + i + "_" + j) as Spine;
                const multiMerge = this.getChildByLabel("multiMerge_" + i + "_" + j) as Spine;
                const multiFxBig = this.getChildByLabel("multiFxBig_" + i + "_" + j) as Spine;

                if (cell) {
                    cell.visible = false;
                    cell.state.clearListeners();
                    multi.visible = false;
                    multi.state.clearListeners();
                    multiMerge.visible = false;
                    multiMerge.state.clearListeners();
                    multiFxBig.visible = false;
                    multiFxBig.state.clearListeners();
                }
                //await wait(50);
            }
        }
    }
}


